import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';

/**
 * SocialTab Component
 * Displays social media statistics with Post Type filtering
 */
const SocialTab = ({ filters, onFilterChange, onViewDetailed }) => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const postTypeCategories = [
        { value: 'regular', label: 'Regular Posts' },
        { value: 'adoption', label: 'Adoption Posts' },
        { value: 'channel', label: 'Channel Posts' },
        { value: 'marketplace', label: 'Marketplace Posts' }
    ];

    useEffect(() => {
        loadSocialStats();
    }, [filters]);

    const loadSocialStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getSocialStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load social stats:', err);
            setError(err.message || 'Failed to load social statistics');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div>
                <div className="h-12 bg-gray-200 rounded w-64 mb-6 animate-pulse"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {Array.from({ length: 6 }).map((_, idx) => (
                        <div key={idx} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                    onClick={loadSocialStats}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    };

    return (
        <div className="w-full">
            <div className="flex justify-end mb-6">
                <button
                    onClick={onViewDetailed}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors font-medium text-sm"
                >
                    View Detailed Analytics <ArrowRight size={16} />
                </button>
            </div>

            {/* Social Stats Cards */}
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">Total Posts</h3>
                        <div className="flex items-baseline gap-2">
                            <span className="stat-value">
                                {(stats?.totalPosts || 0).toLocaleString()}
                            </span>
                        </div>
                        <p className="stat-trend trend-up">
                            <span className="mr-1">↑</span> {stats?.postGrowth || 0}% from previous period
                        </p>
                    </div>
                </div>

                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-secondary)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">Total Engagement</h3>
                        <div className="flex items-baseline gap-2">
                            <span className="stat-value">
                                {(stats?.totalEngagement || 0).toLocaleString()}
                            </span>
                        </div>
                        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                            Likes, comments & shares
                        </p>
                    </div>
                </div>

                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-info)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">Avg. Engagement Rate</h3>
                        <div className="flex items-baseline gap-2">
                            <span className="stat-value">
                                {(stats?.avgEngagementRate || 0).toFixed(1)}%
                            </span>
                        </div>
                        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                            Per post average
                        </p>
                    </div>
                </div>
            </div>

            {/* Engagement Breakdown & Post Types */}
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))' }}>
                {/* Engagement Breakdown */}
                <div className="panel-container">
                    <h3 className="panel-title">
                        <span className="text-xl">📊</span> Engagement Breakdown
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg)', borderRadius: 'var(--radius-md)' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: 'var(--color-text-main)' }}>
                                <span>❤️</span> Likes
                            </span>
                            <span style={{ fontSize: '1.125rem', fontWeight: 700 }}>
                                {(stats?.likes || 0).toLocaleString()}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg)', borderRadius: 'var(--radius-md)' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: 'var(--color-text-main)' }}>
                                <span>💬</span> Comments
                            </span>
                            <span style={{ fontSize: '1.125rem', fontWeight: 700 }}>
                                {(stats?.comments || 0).toLocaleString()}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', backgroundColor: 'var(--color-bg)', borderRadius: 'var(--radius-md)' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: 'var(--color-text-main)' }}>
                                <span>🔄</span> Shares
                            </span>
                            <span style={{ fontSize: '1.125rem', fontWeight: 700 }}>
                                {(stats?.shares || 0).toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Post Type Distribution */}
                <div className="panel-container">
                    <h3 className="panel-title">
                        <span className="text-xl">📈</span> Post Type Distribution
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        {stats?.postsByType && Object.entries(stats.postsByType).map(([type, count]) => {
                            const total = stats.totalPosts || 1;
                            const percentage = Math.min(((count / total) * 100), 100).toFixed(1);
                            const category = postTypeCategories.find(c => c.value === type);

                            return (
                                <div key={type} className="progress-container">
                                    <div className="progress-label-row">
                                        <span style={{ textTransform: 'capitalize', color: 'var(--color-text-main)' }}>
                                            {category?.label || type}
                                        </span>
                                        <span style={{ color: 'var(--color-text-secondary)' }}>
                                            {count.toLocaleString()} ({percentage}%)
                                        </span>
                                    </div>
                                    <div className="progress-track">
                                        <div
                                            className="progress-fill"
                                            style={{ width: `${percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Top Posts */}
            {stats?.topPosts && stats.topPosts.length > 0 && (
                <div className="panel-container">
                    <h3 className="panel-title">🏆 Top Performing Posts</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {stats.topPosts.slice(0, 5).map((post, idx) => (
                            <div
                                key={post.id || idx}
                                style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '1rem', transition: 'background-color 0.2s' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <div className="flex justify-between gap-md">
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontWeight: 500, marginBottom: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {post.content || post.title || 'No content provided'}
                                        </p>
                                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                                            <span>👤 {post.author || 'Unknown'}</span>
                                            <span>📅 {post.created_at ? new Date(post.created_at).toLocaleDateString() : 'N/A'}</span>
                                            <span style={{ padding: '2px 6px', backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary-dark)', borderRadius: '4px', fontSize: '0.7rem', textTransform: 'uppercase' }}>
                                                {post.type || 'Post'}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text-main)' }}>
                                            {(post.engagementCount || 0).toLocaleString()}
                                        </div>
                                        <p style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: 'var(--color-text-secondary)', fontWeight: 600 }}>engagements</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SocialTab;
